import React, { useState, useEffect } from 'react'
import { Col, Form, Button, theme, Tabs, Modal } from 'antd'
import { ContainerCard } from '../../../reusable-components/AgiliteCards'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import CustomRow from '../../../reusable-components/CustomRow'
import BillingMacroForm from './BillingMacroForm'
import useClinicsForm from '../hooks/useClinicsForm'
import { clinicFormTabs } from '../utils/constants'
import { clinicTabsEnums } from '../config/clinic-tabs-enums'
import FloatingFormControlActions from '../../../reusable-components/FloatingFormControlActions'
import {
  basicInfoValid,
  billingMacrosDataValid,
  billingProfileDataValid,
  locationDataValid,
  practiceDetailsDataValid,
  servicesConfigDataValid,
  servicesOfferedDataValid,
  validateClinicFormFields
} from '../utils/lib'
import coreReducer from '../../../../core/utils/reducer'
import store from '../../../../store'
import { useDispatch } from 'react-redux'

const ClinicsForm = ({ data, isNewRecord, refreshView, isWizard, handleWizardNextPrevious }) => {
  const { token } = theme.useToken()
  const dispatch = useDispatch()
  const [unfilledFields, setUnfilledFields] = useState({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 })
  const [recordData, setRecordData] = useState(data ? data : {})
  const [newRecord, setNewRecord] = useState(isNewRecord)
  const [activeTab, setActiveTab] = useState(clinicTabsEnums.BASIC_INFORMATION)
  const [billingProfileSaveAction, setBillingProfileSaveAction] = useState(false)
  const [unsavedChanges, setUnsavedChanges] = useState(false)
  const [resetPoint, setResetPoint] = useState(data)
  const [termsModalVisible, setTermsModalVisible] = useState(false)
  const [editingStepIndex, setEditingStepIndex] = useState(null)
  const [addingStep, setAddingStep] = useState(false)
  const [draggedItem, setDraggedItem] = useState(null)

  const handleTabChange = (key, prev = false) => {
    if (prev) {
      // Convert current tab value to number, subtract 1, then convert back to string
      const currentTabNumber = parseInt(key)
      const previousTabNumber = currentTabNumber - 1

      // Make sure we don't go below the first tab
      if (previousTabNumber >= 1) {
        setActiveTab(previousTabNumber.toString())
      }
    } else {
      setActiveTab(key)
    }
  }
  const {
    basicInfoForm,
    locationContactForm,
    practiceDetailsForm,
    servicesOfferedForm,
    servicesConfigForm,
    billingProfileForm,
    billingMacrosForm,
    handleFormSubmit,
    handleFileChange,
    handleIsActive,
    handleGetAddress,
    handleUploadClick,
    inputRef,
    image,
    loading,
    specialFormState,
    setSpecialFormState,
    bpmProfile,
    bpmConfig,
    setBpmConfig,
    physicalAddress,
    macros,
    setMacros,
    macro,
    setMacro,
    macroIndex,
    macroFormOpen,
    setMacroFormOpen,
    isNewMacro,
    closeTab,
    handleNewMacro,
    handleMacroDelete,
    handleMacroClick,
    schemesLoading,
    schemeOps
  } = useClinicsForm({
    setResetPoint,
    recordData,
    setRecordData,
    newRecord,
    setNewRecord,
    refreshView,
    activeTab,
    isWizard,
    handleWizardNextPrevious,
    handleTabChange,
    setUnsavedChanges
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [activeTab])

  useEffect(() => {
    setUnfilledFields(validateClinicFormFields(unfilledFields, recordData))
    // eslint-disable-next-line
  }, [recordData])

  const handleSaveAction = () => {
    switch (activeTab) {
      case clinicTabsEnums.BASIC_INFORMATION:
        basicInfoForm.submit()
        break
      case clinicTabsEnums.LOCATION_CONTACT:
        locationContactForm.submit()
        break
      case clinicTabsEnums.PRACTICE_DETAILS:
        practiceDetailsForm.submit()
        break
      case clinicTabsEnums.SERVICES_OFFERED:
        servicesOfferedForm.submit()
        break
      case clinicTabsEnums.SERVICES_CONFIG:
        servicesConfigForm.submit()
        break
      case clinicTabsEnums.BILLING_PROFILE:
        billingProfileForm.submit()
        break
      case clinicTabsEnums.BILLING_MACROS:
        if ((!macros || macros.length) === 0 && newRecord) {
          Modal.confirm({
            title: 'No macros set up',
            content:
              'You have not set up any macros for this facility. Are you sure you want to continue? You can come back and change this at any time.',
            okText: 'Create Facility',
            cancelText: 'No, Take me back',
            okButtonProps: { style: { background: token.colorSuccess } },
            onOk: () => {
              handleFormSubmit(recordData)
            }
          })
          break
        }
        handleFormSubmit(recordData)
        break
      default:
        break
    }
  }

  useEffect(() => {
    if (newRecord) {
      dispatch(coreReducer.actions.setUnsavedChanges(true))
    }
    // eslint-disable-next-line
  }, [newRecord])

  const handleValuesChange = (values) => {
    if (newRecord) {
      setRecordData({ ...recordData, ...values })
    } else {
      // Convert to a string for deep comparison
      const isDataChanged = JSON.stringify(resetPoint) !== JSON.stringify({ ...recordData, ...values })

      if (isDataChanged) {
        setRecordData({ ...recordData, ...values })
        setUnsavedChanges(true)
        dispatch(coreReducer.actions.setUnsavedChanges(true))
      } else {
        setUnsavedChanges(false)
        dispatch(coreReducer.actions.setUnsavedChanges(false))
      }
    }
  }

  // An object of form components and their functions, if any of the forms need access to something it can be passed here
  const tabItems = clinicFormTabs({
    token,
    unfilledFields,
    handleNewMacro,
    handleMacroDelete,
    handleMacroClick,
    inputRef,
    handleFileChange,
    handleUploadClick,
    image,
    handleIsActive,
    loading,
    basicInfoForm,
    locationContactForm,
    practiceDetailsForm,
    servicesOfferedForm,
    servicesConfigForm,
    billingProfileForm,
    billingMacrosForm,
    specialFormState,
    setSpecialFormState,
    data: recordData,
    setRecordData,
    handleGetAddress,
    physicalAddress,
    macros,
    bpmProfile,
    bpmConfig,
    setBpmConfig,
    setMacros,
    macro,
    setMacro,
    macroIndex,
    macroFormOpen,
    setMacroFormOpen,
    isNewMacro,
    schemesLoading,
    schemeOps,
    handleTabChange,
    billingProfileSaveAction,
    setBillingProfileSaveAction,
    handleFormSubmit,
    handleValuesChange,
    recordData,
    setUnsavedChanges,
    locationDataValid: locationDataValid(recordData, newRecord),
    basicInfoValid: basicInfoValid(recordData, newRecord),
    practiceDetailsDataValid: practiceDetailsDataValid(recordData, newRecord),
    servicesOfferedDataValid: servicesOfferedDataValid(recordData, newRecord),
    servicesConfigDataValid: servicesConfigDataValid(recordData, newRecord),
    billingProfileDataValid: billingProfileDataValid(recordData, newRecord),
    billingMacrosDataValid: billingMacrosDataValid(recordData, newRecord),
    termsModalVisible,
    newRecord,
    setTermsModalVisible,
    editingStepIndex,
    setEditingStepIndex,
    addingStep,
    setAddingStep,
    draggedItem,
    setDraggedItem
  })

  const handleFormReset = () => {
    setRecordData({ ...resetPoint })
    switch (activeTab) {
      case clinicTabsEnums.BASIC_INFORMATION:
        basicInfoForm.setFieldsValue({ ...resetPoint })
        break
      case clinicTabsEnums.LOCATION_CONTACT:
        locationContactForm.setFieldsValue({ ...resetPoint })
        break
      case clinicTabsEnums.PRACTICE_DETAILS:
        setBpmConfig(resetPoint.bpmConfig)
        practiceDetailsForm.setFieldsValue({ ...resetPoint })
        break
      case clinicTabsEnums.SERVICES_OFFERED:
        servicesOfferedForm.setFieldsValue({ ...resetPoint })
        break
      case clinicTabsEnums.SERVICES_CONFIG:
        servicesConfigForm.setFieldsValue({ ...resetPoint })
        break
      case clinicTabsEnums.BILLING_PROFILE:
        billingProfileForm.setFieldsValue({ ...resetPoint })
        break
      case clinicTabsEnums.BILLING_MACROS:
        setMacros(resetPoint.billingMacros)
        billingMacrosForm.setFieldsValue({ ...resetPoint })
        break
      default:
        break
    }
    setUnsavedChanges(false)
    dispatch(coreReducer.actions.setUnsavedChanges(false))
  }

  const determineFloatingButtonsText = () => {
    if (newRecord) {
      switch (activeTab) {
        case clinicTabsEnums.BASIC_INFORMATION:
          return 'Please fill in the basic information'
        case clinicTabsEnums.LOCATION_CONTACT:
          return 'Please fill in the location contact information'
        case clinicTabsEnums.PRACTICE_DETAILS:
          return 'Please fill in the practice details'
        case clinicTabsEnums.SERVICES_OFFERED:
          return 'Please select at least one service to offer'
        case clinicTabsEnums.SERVICES_CONFIG:
          return 'Please fill in the services configuration'
        case clinicTabsEnums.BILLING_PROFILE:
          return 'Please fill in the billing profile'
        case clinicTabsEnums.BILLING_MACROS:
          return 'Please fill in the billing macros'
        default:
          return 'Create Facility'
      }
    } else {
      return 'Careful you have unsaved changes, please save or reset.'
    }
  }

  // Temporary Exception added for billing macros to solve issue of how it is being rendered
  useEffect(() => {
    handleValuesChange({ billingMacros: macros })
    billingMacrosForm.setFieldsValue({ billingMacros: macros })
    // eslint-disable-next-line
  }, [macros])

  return (
    <ContainerCard title={newRecord ? 'Register a New Clinic' : 'Viewing Clinic'}>
      <style jsx global>{`
        .ant-tabs-dropdown {
          background-color: #ffffff !important;
        }
        .ant-tabs-dropdown .ant-tabs-dropdown-menu {
          background-color: #ffffff !important;
        }
        .ant-tabs-dropdown .ant-tabs-tab {
          background-color: #ffffff !important;
        }
        .ant-tabs-dropdown .ant-tabs-tab:hover {
          background-color: #f5f5f5 !important;
        }
      `}</style>
      <Form form={billingMacrosForm}></Form>
      <CustomRow className='basic-card' id='clinic_form'>
        <Col span={24}>
          <Tabs
            defaultActiveKey={clinicTabsEnums.BASIC_INFORMATION}
            activeKey={activeTab}
            onChange={(key) => {
              if (unsavedChanges) {
                Modal.confirm({
                  title: 'Unsaved Changes',
                  content:
                    'Careful you have unsaved changes, you will lose them if you leave this tab. Please save or reset.',
                  okText: 'Leave anyway',
                  cancelText: 'No, take me back',
                  okButtonProps: { style: { background: token.colorError, color: 'white' } },
                  onOk: () => {
                    handleFormReset()
                    handleTabChange(key)
                  },
                  onCancel: () => {}
                })
              } else {
                handleTabChange(key)
                // handleFormReset()
              }
            }}
            items={tabItems}
            tabBarExtraContent={{
              left: (
                <Form.Item style={{ margin: 0 }}>
                  <Button
                    onClick={() => {
                      if (unsavedChanges || newRecord) {
                        Modal.confirm({
                          title: 'Unsaved Changes',
                          okButtonProps: { style: { background: token.colorError, color: 'white' } },
                          content: newRecord
                            ? 'You are about to leave the facility creation process, you will lose all unsaved changes.'
                            : 'Careful you have unsaved changes, you will lose them if you leave this tab. Please save or reset.',
                          okText: 'Leave anyway',
                          cancelText: 'No, take me back',
                          onOk: () => {
                            closeTab()
                            setUnsavedChanges(false)
                            dispatch(coreReducer.actions.setUnsavedChanges(false))
                          }
                        })
                      } else {
                        closeTab()
                      }
                    }}
                    style={{ marginRight: 12 }}
                  >
                    <FontAwesomeIcon icon={faXmark} color={token.colorBorder} />
                    Close
                  </Button>
                </Form.Item>
              )
            }}
          />
        </Col>
      </CustomRow>
      <BillingMacroForm
        visible={macroFormOpen}
        setVisible={setMacroFormOpen}
        isNew={isNewMacro}
        macro={macro}
        setMacro={setMacro}
        macros={macros}
        setMacros={setMacros}
        index={macroIndex}
      />

      <FloatingFormControlActions
        text={determineFloatingButtonsText()}
        formResetAction={handleFormReset}
        formSaveAction={handleSaveAction}
        loading={loading}
        show={unsavedChanges || newRecord}
        hideReset={newRecord}
        // hideSave={newRecord}
        saveActionText={
          activeTab === clinicTabsEnums.BILLING_MACROS ? 'Create Facility' : newRecord ? 'Next' : 'Save Changes'
        }
        prevActionText={'Back'}
        formPrevAction={() => handleTabChange(activeTab, true)}
        hidePrev={!newRecord || activeTab === clinicTabsEnums.BASIC_INFORMATION}
      />
    </ContainerCard>
  )
}

export default ClinicsForm
